import React from "react";
import {useTranslation} from "@unibuddy/intl";
import * as yup from "yup";
import {ErrorMessage, FormikHelpers} from "formik";
import {Form, FormGroup, Stack, SubmitButton, Text, TextInput} from "@unibuddy/patron";
import {useLocation} from "react-router";

import {IntlNamespaces} from "../../../../translations/types";
import {
    INTL_EMAIL_REQUIRED,
    INTL_INVALID_EMAIL,
    INTL_PASSWORD_REQUIRED,
} from "../../../../translations/validations";
import {INTL_EMAIL, INTL_FORGOT_PASSWORD, INTL_SIGNIN} from "../../../../translations/login";
import {PasswordInput} from "./PasswordInput";
import {AuthFormError} from "../../../../components/AuthFormError/AuthFormError";
import {ThemedRelativeLink} from "./ThemedRelativeLink";

type LoginPageFormProps = {
    handleSubmit: (email: string, password: string) => Promise<void>;
};

type FormValues = {
    email: string;
    password: string;
};

export function LoginPageForm({handleSubmit}: LoginPageFormProps) {
    const {t} = useTranslation([
        IntlNamespaces.SIGNUP,
        IntlNamespaces.SETTINGS,
        IntlNamespaces.LOGIN,
    ]);
    const forgotPasswordText = t(
        `${IntlNamespaces.LOGIN}:${INTL_FORGOT_PASSWORD.key}`,
        INTL_FORGOT_PASSWORD.defaultValue,
    );
    const location = useLocation();

    return (
        <Form
            initialValues={{
                email: "",
                password: "",
                serverError: "",
            }}
            validationSchema={{
                email: yup
                    .string()
                    .email(
                        t(
                            `${IntlNamespaces.VALIDATIONS}:${INTL_INVALID_EMAIL.key}`,
                            INTL_INVALID_EMAIL.defaultValue,
                        ),
                    )
                    .required(
                        t(
                            `${IntlNamespaces.VALIDATIONS}:${INTL_EMAIL_REQUIRED.key}`,
                            INTL_EMAIL_REQUIRED.defaultValue,
                        ),
                    ),
                password: yup
                    .string()
                    .required(
                        t(
                            `${IntlNamespaces.VALIDATIONS}:${INTL_PASSWORD_REQUIRED.key}`,
                            INTL_PASSWORD_REQUIRED.defaultValue,
                        ),
                    ),
            }}
            onSubmit={async (
                values: {
                    email: string;
                    password: string;
                },
                {setSubmitting, setFieldError}: FormikHelpers<FormValues>,
            ) => {
                setSubmitting(true);
                try {
                    await handleSubmit(values.email, values.password);
                } catch (error) {
                    console.error(error);
                    setFieldError("serverError", error.message as string);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(props: {errors: {serverError: string}}) => {
                const {errors} = props;

                return (
                    <Stack space="medium-large">
                        <Stack space="medium">
                            <FormGroup
                                id="email"
                                label={t(
                                    `${IntlNamespaces.LOGIN}:${INTL_EMAIL.key}`,
                                    INTL_EMAIL.defaultValue,
                                )}
                            >
                                <TextInput
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    required
                                />
                            </FormGroup>
                            <PasswordInput name="password" required />
                            <Text>
                                <ThemedRelativeLink
                                    to={{
                                        pathname: location.pathname.replace("login", "reset"),
                                        state: {
                                            nextPathname: location?.state?.nextPathname ?? null,
                                            redirectUrl: location.pathname,
                                        },
                                    }}
                                >
                                    {forgotPasswordText}
                                </ThemedRelativeLink>
                            </Text>
                        </Stack>
                        {errors.serverError && (
                            <ErrorMessage name="serverError" component={AuthFormError} />
                        )}
                        <SubmitButton color="university" block size="lg">
                            {t(
                                `${IntlNamespaces.LOGIN}:${INTL_SIGNIN.key}`,
                                INTL_SIGNIN.defaultValue,
                            )}
                        </SubmitButton>
                    </Stack>
                );
            }}
        </Form>
    );
}
